import type { FormikErrors, FormikTouched, FormikValues } from 'formik'
import type { ComponentType, ReactNode } from 'react'

import { Input } from '../input/Input'

export interface CustomFieldComponentProps {
  name: string
  label: string
  required?: boolean
  width?: number
  errors?: FormikErrors<FormikValues>
  touched?: FormikTouched<FormikValues>
}

export type FieldConfig =
  | {
      kind: 'field'
      name: string
      label: string
      required?: boolean
      width?: number
      autoComplete?: string
    }
  | {
      kind: 'custom'
      name: string
      label: string
      required?: boolean
      width?: number
      Component: ComponentType<CustomFieldComponentProps>
    }
  | {
      kind: 'row'
      fields: Array<{
        name: string
        label: string
        required?: boolean
        width?: number
        autoComplete?: string
      }>
    }

interface FormFieldsProps {
  config: FieldConfig[]
  prefix?: string
  errors?: FormikErrors<FormikValues>
  touched?: FormikTouched<FormikValues>
  isFieldRequired?: (fieldPath: string) => boolean
  rowClassName?: string
}

const getDefaultAutocomplete = (fieldPath: string): string | undefined => {
  const normalizedFieldPath = fieldPath.replace(/^shippingAddress\.|^billingAddress\./, '')

  switch (normalizedFieldPath) {
    case 'firstName':
      return 'given-name'
    case 'lastName':
      return 'family-name'
    case 'fullName':
      return 'name'
    case 'emailAddress':
      return 'email'
    case 'phoneNumber':
      return 'tel'
    case 'company':
      return 'organization'
    case 'addressLine1':
      return 'street-address'
    case 'addressLine2':
      return 'address-line2'
    case 'postalCode':
      return 'postal-code'
    case 'city':
      return 'address-level2'
    case 'province':
      return 'address-level1'
    case 'countryCode':
      return 'country'
    default:
      return undefined
  }
}

export const FormFields = ({
  config,
  prefix,
  errors,
  touched,
  isFieldRequired,
  rowClassName,
}: FormFieldsProps) => {
  const getRequired = (fieldPath: string, fallback: boolean) =>
    isFieldRequired != null ? isFieldRequired(fieldPath) : fallback

  const toFieldPath = (name: string) => (prefix ? `${prefix}.${name}` : name)

  const nodes: ReactNode[] = []

  for (let i = 0; i < config.length; i++) {
    const entry = config[i]

    if (entry.kind === 'row') {
      nodes.push(
        <div key={`${prefix || 'root'}.row.${i}`} className={rowClassName}>
          {entry.fields.map((rowField) => {
            const fieldPath = toFieldPath(rowField.name)
            return (
              <Input
                key={fieldPath}
                name={fieldPath}
                label={rowField.label}
                errors={errors}
                touched={touched}
                required={getRequired(fieldPath, Boolean(rowField.required))}
                width={rowField.width}
                autoComplete={rowField.autoComplete ?? getDefaultAutocomplete(fieldPath)}
              />
            )
          })}
        </div>,
      )
      continue
    }

    if (entry.kind === 'custom') {
      const fieldPath = toFieldPath(entry.name)
      const required = getRequired(fieldPath, Boolean(entry.required))
      nodes.push(
        <entry.Component
          key={fieldPath}
          name={fieldPath}
          label={entry.label}
          required={required}
          width={entry.width}
          errors={errors}
          touched={touched}
        />,
      )
      continue
    }

    const fieldPath = toFieldPath(entry.name)
    nodes.push(
      <Input
        key={fieldPath}
        name={fieldPath}
        label={entry.label}
        errors={errors}
        touched={touched}
        required={getRequired(fieldPath, Boolean(entry.required))}
        width={entry.width}
        autoComplete={entry.autoComplete ?? getDefaultAutocomplete(fieldPath)}
      />,
    )
  }

  return <>{nodes}</>
}
